/**
 * FRONT-2349: which reflection proposals each onboarding recap row stands
 * for, and where each row (and the card) stands. Pure, so every state the
 * card can show is tested without a DOM.
 *
 * The capture stages one Perfect Day proposal per field, so every row but
 * one maps to exactly one proposal. "Key people" is the exception: its row
 * stands for the people list AND, per person, a new Memory entry and a
 * connection to you. It is decided as a group — Leave out rejects all of
 * them — and has no Edit (Marko, 2026-09-24; a person's name is fixed one
 * step later in Reflections, where each new entry has Edit).
 */
import type { ReflectProposal } from '~/hooks/queries/use-reflect';
import { reflectionEditable } from '~/features/memory/reflection-edit-draft';
import type { OnboardingRecapItem } from './tool-signals';

/** Recaps staged before rows carried their field name — mirrors `summarize` in `src/onboarding/tool.ts`. */
const FIELD_BY_LABEL: Record<string, string> = {
  Role: 'identity',
  'What a good day looks like': 'northStar',
  'Must-dos': 'mustDos',
  Focus: 'focus',
  'Key people': 'people',
  'What "done" means': 'loops',
  Autonomy: 'autonomy',
};

/** `partial`: a grouped row where some of its proposals landed and some didn't. */
export type RecapRowStatus = 'pending' | 'saved' | 'edited' | 'left-out' | 'partial';

export interface RecapRow {
  label: string;
  /** What the row reads: the proposal's own stored value when there is one (an edit shows through). */
  value: string;
  /** Every proposal this row decides. Empty when none could be matched: the row is shown, not actionable. */
  proposalIds: number[];
  /** The single proposal an Edit changes, or null when the row has no Edit. */
  editable: ReflectProposal | null;
  /** True for the grouped Key people row: Leave out, no Edit. */
  group: boolean;
  status: RecapRowStatus;
}

function onlyField(p: ReflectProposal): string | null {
  const fields = p.payload?.fields;
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return null;
  const keys = Object.keys(fields);
  return keys.length === 1 ? keys[0] : null;
}

function valueText(p: ReflectProposal, field: string): string | null {
  const v = (p.payload.fields as Record<string, unknown>)[field];
  if (typeof v === 'string') return v;
  if (Array.isArray(v) && v.every((x) => typeof x === 'string')) return v.join(', ');
  return null;
}

function statusOf(proposals: ReflectProposal[]): RecapRowStatus {
  if (proposals.length === 0 || proposals.some((p) => p.status === 'pending')) return 'pending';
  if (proposals.some((p) => p.status === 'applied')) {
    // A grouped row (Key people) can land only in part — e.g. a person's
    // connection declined because the entry it pointed at was gone.
    if (proposals.some((p) => p.status === 'rejected')) return 'partial';
    return proposals.some((p) => p.payload?.userEdited === true) ? 'edited' : 'saved';
  }
  return 'left-out';
}

export function buildRecapRows(
  items: OnboardingRecapItem[],
  allProposals: ReflectProposal[] | undefined,
  runId: number,
): RecapRow[] {
  const run = (allProposals ?? []).filter((p) => p.runId === runId);
  const perField = new Map<string, ReflectProposal>();
  for (const p of run) {
    if (p.kind !== 'perfect_day_update') continue;
    const f = onlyField(p);
    if (f && !perField.has(f)) perField.set(f, p);
  }
  return items.map((item) => {
    const field = item.field ?? FIELD_BY_LABEL[item.label] ?? null;
    const own = field ? perField.get(field) : undefined;
    if (field === 'people') {
      // The people list plus each person's entry and connection — the only
      // other kinds the onboarding capture stages.
      const group = run.filter(
        (p) => p === own || p.kind === 'entity_create' || p.kind === 'relationship_create',
      );
      return {
        label: item.label,
        value: item.value,
        proposalIds: group.map((p) => p.id),
        editable: null,
        group: true,
        status: statusOf(group),
      };
    }
    return {
      label: item.label,
      value: (own && field && valueText(own, field)) ?? item.value,
      proposalIds: own ? [own.id] : [],
      editable: own && own.status === 'pending' && reflectionEditable(own) ? own : null,
      group: false,
      status: statusOf(own ? [own] : []),
    };
  });
}

export type RecapDecision = 'accepted' | 'partial' | 'declined' | null;

/**
 * Where the whole card stands, from the run's actual proposal statuses. Any
 * proposal still pending means the card is still open — a row saved on its
 * own is not the card's decision (FRONT-2349; before per-row saves, one
 * applied proposal meant the whole run had been accepted).
 */
export function deriveRecapDecision(
  allProposals: Pick<ReflectProposal, 'runId' | 'status'>[] | undefined,
  runId: number,
): RecapDecision {
  const run = allProposals?.filter((p) => p.runId === runId) ?? [];
  if (run.length === 0 || run.some((p) => p.status === 'pending')) return null;
  const applied = run.some((p) => p.status === 'applied');
  const rejected = run.some((p) => p.status === 'rejected');
  if (applied && rejected) return 'partial';
  return applied ? 'accepted' : 'declined';
}

/**
 * The badge a row shows, or null. While the card is open, only settled rows
 * carry one. Once decided, a badge that would just repeat the header on
 * every row is left off; an edited row always says so.
 */
export function rowBadge(row: RecapRow, rows: RecapRow[], decided: boolean): string | null {
  if (row.status === 'pending') return null;
  if (row.status === 'edited') return 'Saved your version';
  if (decided && rows.every((r) => r.status === row.status)) return null;
  if (row.status === 'partial') return 'Partly saved';
  return row.status === 'saved' ? 'Saved' : 'Left out';
}

export function recapHeader(decision: RecapDecision, rows: RecapRow[]): string {
  if (decision === null) return 'Here’s what I’ll remember';
  if (decision === 'declined') return 'Not saved';
  if (decision === 'partial') return 'Saved what you kept';
  return rows.some((r) => r.status === 'edited') ? 'Saved to memory, with your edits' : 'Saved to memory';
}
